'use client';

import type { FormEventHandler, KeyboardEventHandler } from 'react';
import Link from 'next/link';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { ButtonProgressBar, LookupSearchPanel, SearchFilterCard } from '@/components/ui/design-system';
import { InputWithIcon } from '@/components/ui/forms/input-with-icon';

interface CompanyLookupSearchPanelProps {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onSearchSubmit: FormEventHandler<HTMLFormElement>;
  isSearching: boolean;
  onCancelSearch: () => void;
  searchProgress: number;
  locationInput: string;
  onLocationInputChange: (value: string) => void;
  onLocationInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  locationFilters: string[];
  onAddLocation: () => void;
  onRemoveLocation: (location: string) => void;
  maxLocationFilters: number;
  className?: string;
}

export function CompanyLookupSearchPanel({
  searchQuery,
  onSearchQueryChange,
  onSearchSubmit,
  isSearching,
  onCancelSearch,
  searchProgress,
  locationInput,
  onLocationInputChange,
  onLocationInputKeyDown,
  locationFilters,
  onAddLocation,
  onRemoveLocation,
  maxLocationFilters,
  className,
}: CompanyLookupSearchPanelProps) {
  return (
    <LookupSearchPanel onSubmit={onSearchSubmit} className={className}>
      <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center border-b border-border">
        <InputWithIcon
          icon={Search}
          containerClassName="flex-1"
          placeholder="Search companies (e.g., Meta, Rippling, NVIDIA)..."
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          className="h-9"
          rightSlot={searchQuery.trim() ? (
            <button
              type="button"
              onClick={() => onSearchQueryChange('')}
              className="text-muted-foreground transition-colors hover:text-destructive"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : undefined}
        />
        <Button
          size="sm"
          type={isSearching ? 'button' : 'submit'}
          onClick={isSearching ? onCancelSearch : undefined}
          className="border h-9 px-3 text-xs relative overflow-hidden"
          style={{ boxShadow: 'var(--shadow-warm-sm)' }}
          disabled={!isSearching && !searchQuery.trim()}
        >
          {isSearching ? 'Stop search' : 'Search companies'}
          {isSearching && <ButtonProgressBar progress={searchProgress} />}
        </Button>
        <Link
          href="/search"
          className="self-center whitespace-nowrap text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary sm:ml-auto"
        >
          Try Smart Search →
        </Link>
      </div>

      <SearchFilterCard
        className="p-3"
        title="Locations"
        placeholder="Add location (e.g. New York, California, Remote)"
        max={maxLocationFilters}
        values={locationFilters}
        inputValue={locationInput}
        onInputChange={onLocationInputChange}
        onInputKeyDown={onLocationInputKeyDown}
        onAdd={onAddLocation}
        onRemove={onRemoveLocation}
      />
    </LookupSearchPanel>
  );
}
